import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getThemeByDealership, defaultTheme, generateThemeCSS } from '../config/dealershipThemes';
import { 
  getColorsForDealership, 
  defaultColors, 
  generateCustomColorCSS,
  darkModeOptions,
  getDarkModeOption
} from '../config/colorPalettes';

const ThemeContext = createContext();

// Predefined background colors for dark mode color picker swatches
export const darkBackgroundColors = [
  { id: 'black', name: 'Pure Black', color: '#000000' },
  { id: 'charcoal', name: 'Charcoal', color: '#0A0A0C' },
  { id: 'dark-blue', name: 'Dark Blue', color: '#0A0F1A' },
  { id: 'dark-purple', name: 'Dark Purple', color: '#0D0A14' },
  { id: 'dark-green', name: 'Dark Green', color: '#0A120D' },
  { id: 'dark-grey', name: 'Dark Grey', color: '#121212' },
  { id: 'slate', name: 'Slate', color: '#0F172A' },
  { id: 'midnight', name: 'Midnight', color: '#020617' },
  { id: 'navy', name: 'Navy', color: '#0C1222' },
  { id: 'dark-teal', name: 'Dark Teal', color: '#0A1414' },
];

export function ThemeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return saved === 'true';
    }
    // Default to dark mode
    return true;
  });

  // Glass morphism settings - 0 to 100 for transparency percentage
  const [glassTransparency, setGlassTransparency] = useState(() => {
    const saved = localStorage.getItem('glassTransparency');
    return saved ? parseInt(saved) : 75; // Default 75% transparency (similar to login screen)
  });

  // Glass blur - 0 to 100 (maps to 0-40px blur)
  const [glassBlur, setGlassBlur] = useState(() => {
    const saved = localStorage.getItem('glassBlur');
    return saved ? parseInt(saved) : 40; // Default 40% (16px blur)
  });

  // Background color for dark mode
  const [backgroundColor, setBackgroundColor] = useState(() => {
    return localStorage.getItem('backgroundColor') || '#0A0A0C';
  });

  // Dark mode variant (kept for backward compatibility but will be overridden by custom settings)
  const [darkModeVariant, setDarkModeVariant] = useState(() => {
    return localStorage.getItem('darkModeVariant') || 'black-75';
  });

  const [dealership, setDealership] = useState(() => {
    return localStorage.getItem('dealership') || null;
  });

  const [dealershipTheme, setDealershipTheme] = useState(() => {
    const saved = localStorage.getItem('dealership');
    return getThemeByDealership(saved);
  });

  // Custom colors state
  const [customColors, setCustomColors] = useState(() => {
    const saved = localStorage.getItem('customColors');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });

  // Track if user has customized colors (vs using dealership default)
  const [isCustomized, setIsCustomized] = useState(() => {
    return localStorage.getItem('isColorCustomized') === 'true';
  });

  // Get effective colors (custom or dealership-based)
  const getEffectiveColors = () => {
    if (isCustomized && customColors) {
      return customColors;
    }
    return getColorsForDealership(dealership);
  };

  // Get current dark mode settings
  const getDarkModeColors = () => {
    return getDarkModeOption(darkModeVariant);
  };

  // Helper to convert hex to HSL for Tailwind CSS variables
  const hexToHSL = useCallback((hex) => {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;
    
    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }, []);

  useEffect(() => {
    // Apply dark class to html element
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);

  // Helper to convert hex to rgba with custom alpha
  const hexToRgba = useCallback((hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }, []);

  // Calculate blur in pixels from percentage (0-100 maps to 0-40px)
  const getBlurPx = useCallback(() => {
    return Math.round((glassBlur / 100) * 40);
  }, [glassBlur]);

  // Calculate opacity from transparency percentage
  // Higher transparency = lower opacity (more see-through)
  // 0% transparency = 1.0 opacity (solid), 100% transparency = 0.1 opacity (very transparent)
  const getOpacity = useCallback(() => {
    // Map: 0% transparency -> 0.95 opacity, 100% transparency -> 0.15 opacity
    return 0.95 - (glassTransparency / 100) * 0.80;
  }, [glassTransparency]);

  // Apply custom glass morphism settings
  useEffect(() => {
    if (isDarkMode) {
      const opacity = getOpacity();
      const blurPx = getBlurPx();
      
      // Set background color
      document.documentElement.style.setProperty('--dark-background', backgroundColor);
      document.documentElement.style.setProperty('--dark-background-hsl', hexToHSL(backgroundColor));
      
      // Calculate glass colors based on background color with custom opacity
      const glassBg = hexToRgba(backgroundColor, opacity);
      const glassCard = hexToRgba(backgroundColor, opacity * 0.95);
      const glassSidebar = hexToRgba(backgroundColor, opacity + 0.05);
      const glassHeader = hexToRgba(backgroundColor, opacity);
      
      // Apply glass morphism CSS variables
      document.documentElement.style.setProperty('--glass-bg', glassBg);
      document.documentElement.style.setProperty('--glass-card', glassCard);
      document.documentElement.style.setProperty('--glass-sidebar', glassSidebar);
      document.documentElement.style.setProperty('--glass-header', glassHeader);
      document.documentElement.style.setProperty('--glass-blur', `${blurPx}px`);
      document.documentElement.style.setProperty('--glass-opacity', opacity.toString());
      
      // Also set card bg for consistency
      document.documentElement.style.setProperty('--dark-card-bg', backgroundColor);
      document.documentElement.style.setProperty('--dark-card-hsl', hexToHSL(backgroundColor));
      
      // Text colors (keep white for dark mode)
      document.documentElement.style.setProperty('--dark-text-heading', '#FFFFFF');
      document.documentElement.style.setProperty('--dark-text-body', '#E5E5E5');
      document.documentElement.style.setProperty('--dark-text-subheading', '#A3A3A3');
      document.documentElement.style.setProperty('--dark-border-color', '#1A1A1F');
    }
    
    // Persist settings
    localStorage.setItem('glassTransparency', glassTransparency.toString());
    localStorage.setItem('glassBlur', glassBlur.toString());
    localStorage.setItem('backgroundColor', backgroundColor);
    localStorage.setItem('darkModeVariant', darkModeVariant);
  }, [isDarkMode, glassTransparency, glassBlur, backgroundColor, darkModeVariant, getOpacity, getBlurPx, hexToRgba, hexToHSL]);

  useEffect(() => {
    // Update dealership theme
    const theme = getThemeByDealership(dealership);
    setDealershipTheme(theme);
    
    // Apply CSS variables to root
    const cssVars = generateThemeCSS(theme);
    Object.entries(cssVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
    
    // If not customized, update colors based on dealership
    if (!isCustomized) {
      const dealerColors = getColorsForDealership(dealership);
      applyColorCSS(dealerColors);
    }
    
    // Store dealership
    if (dealership) {
      localStorage.setItem('dealership', dealership);
    }
  }, [dealership, isCustomized]);

  // Apply custom colors CSS
  const applyColorCSS = (colors) => {
    const cssVars = generateCustomColorCSS(colors, isDarkMode);
    Object.entries(cssVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  };

  // Re-apply colors when dark mode changes
  useEffect(() => {
    const colors = getEffectiveColors();
    applyColorCSS(colors);
  }, [isDarkMode, customColors, isCustomized, dealership]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const setDarkMode = (enabled) => {
    setIsDarkMode(enabled);
  };

  // Update glass settings
  const updateGlassTransparency = (value) => {
    setGlassTransparency(value);
  };

  const updateGlassBlur = (value) => {
    setGlassBlur(value);
  };

  const updateBackgroundColor = (color) => {
    setBackgroundColor(color);
  };

  const updateDarkModeVariant = (variant) => {
    setDarkModeVariant(variant);
  };

  // Reset glass settings to default
  const resetGlassSettings = () => {
    setGlassTransparency(75);
    setGlassBlur(40);
    setBackgroundColor('#0A0A0C');
  };

  const updateDealership = (newDealership) => {
    setDealership(newDealership);
    
    // If this is first login and no customization, set dealership colors as default
    if (!isCustomized && !customColors) {
      const dealerColors = getColorsForDealership(newDealership);
      setCustomColors(dealerColors);
      localStorage.setItem('customColors', JSON.stringify(dealerColors));
    }
  };

  // Update a single custom color
  const updateCustomColor = (key, value) => {
    const newColors = {
      ...(customColors || getEffectiveColors()),
      [key]: value
    };
    setCustomColors(newColors);
    setIsCustomized(true);
    localStorage.setItem('customColors', JSON.stringify(newColors));
    localStorage.setItem('isColorCustomized', 'true');
    applyColorCSS(newColors);
  };

  // Apply a complete palette
  const applyPalette = (paletteColors) => {
    setCustomColors(paletteColors);
    setIsCustomized(true);
    localStorage.setItem('customColors', JSON.stringify(paletteColors));
    localStorage.setItem('isColorCustomized', 'true');
    applyColorCSS(paletteColors);
  };

  // Reset to dealership default
  const resetToDefault = () => {
    const dealerColors = getColorsForDealership(dealership);
    setCustomColors(dealerColors);
    setIsCustomized(false);
    localStorage.setItem('customColors', JSON.stringify(dealerColors));
    localStorage.setItem('isColorCustomized', 'false');
    applyColorCSS(dealerColors);
  };

  // Reset to Fuelogic default (if no dealership preference)
  const resetToFuelogic = () => {
    setCustomColors(defaultColors);
    setIsCustomized(false);
    localStorage.setItem('customColors', JSON.stringify(defaultColors));
    localStorage.setItem('isColorCustomized', 'false');
    applyColorCSS(defaultColors);
  };

  return (
    <ThemeContext.Provider value={{ 
      isDarkMode, 
      toggleDarkMode,
      setDarkMode,
      darkModeVariant,
      updateDarkModeVariant,
      getDarkModeColors,
      darkModeOptions,
      // Glass morphism settings
      glassTransparency,
      glassBlur,
      backgroundColor,
      updateGlassTransparency,
      updateGlassBlur,
      updateBackgroundColor,
      resetGlassSettings,
      // Dealership and colors
      dealership, 
      updateDealership, 
      dealershipTheme,
      defaultTheme,
      // Color customization exports
      customColors: customColors || getEffectiveColors(),
      isCustomized,
      updateCustomColor,
      applyPalette,
      resetToDefault,
      resetToFuelogic,
      getEffectiveColors
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
